import React, { useEffect } from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { setSupportRequestAction } from '../Configurator/actions/furniture';
import {
  BLACK_COLOR,
  CabinetSupport,
  Material,
} from '../../lib/modelBuilder/constants';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox } from '@material-ui/core';
import { FiMoreVertical } from 'react-icons/fi';
import { CustomPiece } from '../../lib/modelBuilder/pieces/CustomPiece';
import { Batten } from '../../lib/modelBuilder/pieces/Batten';
import { createStructuredSelector } from 'reselect';
import { RootState } from './types';
import { makeSelectLastHistoryCabinet } from '../Configurator/selectors/history';
import { Cabinet } from '../../lib/modelBuilder/entities/Cabinet';

interface Props {
  display: boolean;
  setDisplayCustom: any;
  foot: CustomPiece<Batten> | null;
}

const ValueChangeListener = () => {
  const { submitForm, values } = useFormikContext();

  useEffect(() => {
    // @ts-ignore
    if (values.height) {
      submitForm();
    }
  }, [values, submitForm]);

  return null;
};
interface StateProps {
  cabinet: Cabinet;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  cabinet: makeSelectLastHistoryCabinet(),
});

const FeetsDimensionsForm = ({ display, setDisplayCustom, foot }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cabinet } = useSelector(stateSelector);

  if (!foot) return null;

  if (!display) {
    return (
      <div className="flex items-center justify-center ml-1">
        <button
          onClick={() => {
            setDisplayCustom(true);
          }}
          data-tip={t('Customize')}
        >
          <FiMoreVertical className="h-5 w-5 text-gray-700 -mt-1" />
        </button>
      </div>
    );
  }

  return (
    <Formik
      initialValues={{
        height: `${foot.height ?? ''}`,
        adjustable: cabinet.support === CabinetSupport.AdjustableFeet,
      }}
      validationSchema={Yup.object({
        height: Yup.number()
          .min(40, t('The height of the feets must be 40mm or more'))
          .max(200, t('The height of the feets must be 200mm or less'))
          .required(t('The value is required')),
      })}
      onSubmit={(values, { setSubmitting }) => {
        let material = Material.Wood;
        if (values.adjustable) {
          if (cabinet.getCorner()?.color?.reference === BLACK_COLOR) {
            material = Material.Black;
          } else {
            material = Material.Aluminium;
          }
        }
        const support =
          material === Material.Wood
            ? CabinetSupport.Feet
            : CabinetSupport.AdjustableFeet;
        dispatch(
          setSupportRequestAction(
            support,
            false,
            parseInt(values.height),
            material,
          ),
        );
      }}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      {({ values, handleChange, errors, touched, setFieldValue }) => (
        <div className="flex flex-col items-center">
          <div className="text-sm mb-1">{t('Customize')}</div>
          <div className="flex items-center">
            <input
              id="height"
              name="height"
              type="number"
              onChange={handleChange}
              value={values.height}
              className={`border py-1 my-1 px-2 w-20 text-base ${
                touched.height && errors.height
                  ? 'border-red'
                  : 'border-gray-400'
              }`}
            />
            <div className="flex flex-col">
              <div className="text-xs ml-1 text-gray-800">mm</div>
            </div>
          </div>
          {errors.height && (
            <div className="text-red-dark italic text-sm ">{errors.height}</div>
          )}

          <div className="-mt-1">
            <label className="flex items-center">
              <Checkbox
                name="adjustable"
                value="true"
                checked={values.adjustable}
                onChange={() => {
                  setFieldValue('adjustable', !values.adjustable);
                }}
                color="primary"
              />
              <div className="text-sm text-gray-800 -ml-2">
                {t('Adjustable feet')}
              </div>
            </label>
          </div>
          <ValueChangeListener />
        </div>
      )}
    </Formik>
  );
};

export default FeetsDimensionsForm;
